/**
 * riskInterpreter.js - GWAS-aware risk categorization
 *
 * Changes in this version:
 * 1. Added filterToCarriedAssociations() — returns SNP with GWAS filtered to
 *    only associations relevant to the user's actual genotype.
 * 2. Added hasCarriedAssociations() — used by FilterBar "My Genotype" toggle.
 * 3. Added getGwasOnlyCategory() — categorises SNPs that have GWAS data but
 *    no ClinVar entry (magnitude == null), so they appear correctly in filters.
 * 4. interpretClinicalSignificance() now falls through to GWAS-only categorisation
 *    when there is no ClinVar significance string, instead of returning 'other'.
 * 5. copiesCarried === null is treated as "unknown" throughout — these
 *    associations are not silently excluded by carried-only filters.
 * 6. generateRiskMessage() now:
 *    - Suppresses GxE interaction traits with a conditional note instead of
 *      a meaningless percentage ("99.9% lower odds of pulmonary function
 *      (smoking interaction)" → "Effect depends on environmental factors")
 *    - Guards against beta coefficients misidentified as ORs (val < 0.01 or
 *      val > 100) which produce impossible-looking percentages
 * 7. Added 'neutral' category for GWAS-only SNPs with small/negligible effect
 *    sizes (OR between 0.83–1.2), replacing the misleading 'uncertain' label
 *    for SNPs that are genuinely just not meaningful.
 */

/**
 * Detect if a trait is a quantitative measurement vs a disease/condition
 */
export const isQuantitativeTrait = (trait) => {
  if (!trait) return false;
  const lower = trait.toLowerCase();
  
  const measurementKeywords = [
    // Units / generic measurement words
    'level', 'levels', 'measurement', 'concentration',
    'size', 'height', 'weight', 'length', 'area',
    'volume', 'count', 'number', 'ratio', 'rate',
    'speed', 'age', 'duration', 'time',
    'density', 'pressure', 'capacity', 'score', 'index',
    // Specific biomarkers / lab values commonly reported as betas in GWAS
    'cholesterol', 'glucose', 'triglyceride', 'insulin',
    'bmi', 'body mass', 'adiposity', 'obesity measures',
    'phospholipid', 'lipid', 'lipoprotein', 'hdl', 'ldl', 'vldl',
    'albumin', 'creatinine', 'urate', 'uric acid', 'magnesium',
    'calcium', 'potassium', 'sodium', 'iron', 'ferritin',
    'haemoglobin', 'hemoglobin', 'platelet', 'white blood cell',
    'red blood cell', 'leucocyte', 'lymphocyte', 'neutrophil',
    'testosterone', 'cortisol', 'thyroid', 'tsh', 'vitamin',
    'c-reactive protein', 'crp', 'interleukin',
    'forced expiratory', 'fev', 'fvc', 'lung function', 'pulmonary function',
    'blood pressure', 'systolic', 'diastolic', 'heart rate',
    'waist', 'hip circumference', 'fat mass', 'lean mass',
    'bone mineral', 'grip strength', 'walking speed',
    'intelligence', 'cognitive', 'educational attainment',
    'years of schooling',
  ];
  
  return measurementKeywords.some(kw => lower.includes(kw));
};

// ---------------------------------------------------------------------------
// Genotype-relevance utilities
// ---------------------------------------------------------------------------

/**
 * Return a copy of the SNP with GWAS associations filtered to only those
 * where the user actually carries the relevant allele (copiesCarried > 0).
 *
 * Associations with copiesCarried === null (unknown allele) are preserved
 * rather than excluded, so ambiguous data isn't silently lost.
 */
export const filterToCarriedAssociations = (snp) => {
  if (!snp.gwas?.topAssociations) return snp;

  return {
    ...snp,
    gwas: {
      ...snp.gwas,
      topAssociations: snp.gwas.topAssociations.filter(
        a => a.copiesCarried === null || a.copiesCarried > 0
      ),
    }
  };
};

/**
 * Returns true if the user carries at least one GWAS risk/protective allele
 * for this SNP, OR if there are associations with unknown allele status.
 *
 * Used by the "My Genotype" FilterBar toggle to surface only personally
 * relevant variants and reduce confusion from population-level associations
 * the user doesn't actually carry.
 */
export const hasCarriedAssociations = (snp) => {
  if (!snp.gwas?.topAssociations?.length) return false;
  return snp.gwas.topAssociations.some(
    a => a.copiesCarried === null || a.copiesCarried > 0
  );
};

/**
 * Determine category purely from GWAS data (for SNPs without ClinVar entries).
 * Uses the most impactful carried association to decide direction.
 * GxE associations are excluded — their ORs are not meaningful in isolation.
 *
 * OR thresholds:
 *   > 1.5 or < 0.67  → risk / protective (strong signal)
 *   1.2–1.5 or 0.67–0.83 → risk / protective (moderate signal)
 *   0.83–1.2         → neutral (small effect, not meaningfully risk or protective)
 */
const getGwasOnlyCategory = (snp) => {
  const associations = snp.gwas?.topAssociations;
  if (!associations?.length) return null;

  // Exclude GxE — their ORs are conditional on environment, not usable here
  const carried = associations
    .filter(a => !a.gxe && (a.copiesCarried === null || a.copiesCarried > 0))
    .filter(a => {
      const odds = a.userEffectiveOdds || a.orBeta;
      if (!odds) return false;
      if (odds < 0.01 || odds > 100) return false; // extreme beta guard
      // For quantitative traits, values outside 0.5–2.0 are almost certainly
      // beta coefficients stored in the OR field — exclude them from categorisation
      if (isQuantitativeTrait(a.trait) && (odds < 0.5 || odds > 2.0)) return false;
      return true;
    })
    .sort((a, b) => {
      const aImpact = Math.abs(1 - (a.userEffectiveOdds || 1));
      const bImpact = Math.abs(1 - (b.userEffectiveOdds || 1));
      return bImpact - aImpact;
    });

  if (carried.length === 0) return null;

  const top = carried[0];
  const odds = top.userEffectiveOdds || top.orBeta;

  if (!odds) return null;

  if (odds > 1.5) {
    return {
      category: 'risk',
      severity: 3,
      label: 'Risk Factor',
      color: 'yellow',
      icon: '🟡',
      description: 'GWAS association with increased risk'
    };
  }

  if (odds > 1.2) {
    return {
      category: 'risk',
      severity: 3,
      label: 'Risk Factor',
      color: 'yellow',
      icon: '🟡',
      description: 'GWAS association with moderately increased risk'
    };
  }

  if (odds < 0.67) {
    return {
      category: 'protective',
      severity: 1,
      label: 'Protective',
      color: 'green',
      icon: '🟢',
      description: 'GWAS association with decreased risk'
    };
  }

  if (odds < 0.83) {
    return {
      category: 'protective',
      severity: 1,
      label: 'Protective',
      color: 'green',
      icon: '🟢',
      description: 'GWAS association with moderately decreased risk'
    };
  }

  // OR between 0.83–1.2: GWAS data exists but effect is negligible — neutral
  return {
    category: 'neutral',
    severity: 1.5,
    label: 'Neutral',
    color: 'gray',
    icon: '⚪',
    description: 'GWAS association with small or no meaningful effect'
  };
};

// ---------------------------------------------------------------------------
// Core interpretation
// ---------------------------------------------------------------------------

/**
 * Interpret clinical significance WITH GWAS awareness.
 *
 * Priority order:
 * 1. ClinVar pathogenic / likely-pathogenic (definitive disease signal)
 * 2. ClinVar risk factor / drug response
 * 3. Significant GWAS increased-risk signal (even if ClinVar says benign)
 * 4. ClinVar benign — but downgrade to likely-inactive for low-impact het
 * 5. Significant GWAS protective signal
 * 6. ClinVar uncertain / conflicting
 * 7. GWAS-only (no ClinVar) — derived from getGwasOnlyCategory()
 *    → 'neutral' when OR is 0.83–1.2 (small effect)
 *    → 'risk' / 'protective' for larger effects
 * 8. Default fallback
 */
export const interpretClinicalSignificance = (snp) => {
  const clinSig = snp.clinicalSignificance || '';
  const sig = clinSig.toLowerCase();

  // All valid carried GWAS associations — excluding GxE and likely-beta-coefficient values
  const validCarried = (snp.gwas?.topAssociations || []).filter(a => {
    if (a.gxe) return false;
    if (a.copiesCarried !== null && a.copiesCarried === 0) return false;
    const odds = a.userEffectiveOdds || a.orBeta;
    if (!odds || odds < 0.01 || odds > 100) return false;
    // Quantitative traits with values outside 0.5–2.0 are betas, not ORs
    if (isQuantitativeTrait(a.trait) && (odds < 0.5 || odds > 2.0)) return false;
    return true;
  });

  // Pick the single most impactful association by OR deviation from 1.0,
  // but break ties by preferring risk (OR > 1) over protective (OR < 1):
  // a 35% increased cancer risk should never be hidden behind a smaller
  // protective association on a different trait.
  const carriedAssoc = validCarried.length > 0
    ? validCarried.reduce((best, a) => {
        const aOdds = a.userEffectiveOdds || a.orBeta;
        const bOdds = best.userEffectiveOdds || best.orBeta;
        const aDev  = Math.abs(1 - aOdds);
        const bDev  = Math.abs(1 - bOdds);
        if (aDev > bDev) return a;
        if (aDev < bDev) return best;
        return aOdds > 1 ? a : best;
      })
    : null;

  const hasSignificantGWAS = carriedAssoc &&
    (carriedAssoc.userEffectiveOdds > 1.2 || carriedAssoc.userEffectiveOdds < 0.83);

  // Track whether any association is a risk signal — so a protective OR on
  // a trivial trait never overrides a cancer risk on a different one.
  const hasCarriedRisk = validCarried.some(a => (a.userEffectiveOdds || a.orBeta) > 1.2);
  const hasCarriedProtective = validCarried.some(a => (a.userEffectiveOdds || a.orBeta) < 0.83);

  // 1. ClinVar pathogenic (definitive)
  if (sig.includes('pathogenic') && !sig.includes('likely') && !sig.includes('benign')) {
    return {
      category: 'pathogenic',
      severity: 5,
      label: 'Pathogenic',
      color: 'red',
      icon: '🔴',
      description: 'Disease-causing variant'
    };
  }

  // 2. ClinVar likely pathogenic
  if (sig.includes('likely pathogenic') && !sig.includes('benign')) {
    return {
      category: 'likely-pathogenic',
      severity: 4,
      label: 'Likely Pathogenic',
      color: 'orange',
      icon: '🟠',
      description: 'Probably disease-causing'
    };
  }

  // 3. ClinVar risk factor
  if (sig.includes('risk factor') || sig.includes('risk allele')) {
    return {
      category: 'risk',
      severity: 3,
      label: 'Risk Factor',
      color: 'yellow',
      icon: '🟡',
      description: 'Increases disease risk'
    };
  }

  // 4. Drug response
  if (sig.includes('drug response') || sig.includes('pharmacogenetic')) {
    return {
      category: 'drug-response',
      severity: 2,
      label: 'Drug Response',
      color: 'purple',
      icon: '💊',
      description: 'Affects medication response'
    };
  }

  // 5. GWAS shows significant increased risk (even if ClinVar says benign).
  //    Use hasCarriedRisk rather than just checking carriedAssoc direction, so
  //    a protective OR on a minor trait can't mask a risk OR on a serious one.
  if (hasCarriedRisk) {
    return {
      category: 'risk',
      severity: 3,
      label: 'Risk Factor',
      color: 'yellow',
      icon: '🟡',
      description: 'GWAS association with increased risk'
    };
  }

  // 6. ClinVar benign
  if (sig.includes('benign')) {
    const isHeterozygous = snp.genotype?.length === 2 && snp.genotype[0] !== snp.genotype[1];
    const isLowMagnitude = snp.magnitude != null && snp.magnitude <= 1;

    if (isHeterozygous && isLowMagnitude && !hasSignificantGWAS) {
      return {
        category: 'likely-inactive',
        severity: 0.5,
        label: 'Likely Inactive',
        color: 'gray',
        icon: '⚪',
        description: 'One copy of benign variant'
      };
    }

    return {
      category: 'protective',
      severity: 1,
      label: sig.includes('likely') ? 'Likely Benign' : 'Benign',
      color: 'green',
      icon: '🟢',
      description: 'Harmless variant'
    };
  }

  // 7. GWAS shows significant protective effect
  if (hasCarriedProtective) {
    return {
      category: 'protective',
      severity: 1,
      label: 'Protective',
      color: 'green',
      icon: '🟢',
      description: 'GWAS association with decreased risk'
    };
  }

  // 8. ClinVar uncertain / conflicting
  if (sig.includes('uncertain') || sig.includes('conflicting')) {
    return {
      category: 'uncertain',
      severity: 2,
      label: 'Uncertain',
      color: 'blue',
      icon: '🔵',
      description: 'Clinical impact unclear'
    };
  }

  // 9. No ClinVar significance — derive category from GWAS alone.
  //    getGwasOnlyCategory() returns 'neutral' for OR 0.83–1.2,
  //    'risk' or 'protective' for larger effects.
  if (!sig && snp.gwas?.hasData) {
    const gwasCategory = getGwasOnlyCategory(snp);
    if (gwasCategory) return gwasCategory;
  }

  // 10. Default — treat as neutral rather than 'other' so SNPs don't
  //     disappear into an invisible filter bucket
  return {
    category: 'neutral',
    severity: 1.5,
    label: 'Neutral',
    color: 'gray',
    icon: '⚪',
    description: 'No significant clinical association found'
  };
};

// ---------------------------------------------------------------------------
// Message generation
// ---------------------------------------------------------------------------

/**
 * Generate a human-readable risk message from a single GWAS association.
 * Uses userEffectiveOdds (genotype-adjusted) rather than raw orBeta.
 *
 * Guards:
 * - GxE associations: show a conditional note instead of a percentage,
 *   because the OR is only meaningful if the user has a specific environmental
 *   exposure (e.g. smokes, has elevated BMI). Without that context "99.9%
 *   lower odds" is actively misleading.
 * - Extreme ORs (< 0.01 or > 100): almost certainly a beta coefficient stored
 *   in the OR field by the GWAS Catalog. Suppress the percentage framing.
 */
export const generateRiskMessage = (association, trait) => {
  if (!association || !trait) return null;

  // GxE: effect is conditional on environmental exposure — don't show OR %
  if (association.gxe) {
    const envMatch = trait.match(/\(([^)]+interaction[^)]*)\)/i);
    const envContext = envMatch ? ` (${envMatch[1]})` : '';
    return `Associated with ${trait.replace(/\s*\([^)]*interaction[^)]*\)/i, '').trim()}${envContext} — effect depends on environmental factors`;
  }

  const val = parseFloat(association.userEffectiveOdds || association.orBeta);
  if (!val) return null;

  // For quantitative traits (continuous lab values like cholesterol, Mg, HDL),
  // the GWAS Catalog often stores beta coefficients in the OR field — these are
  // effect sizes in SD units, not multiplicative odds ratios. A beta of 0.01
  // looks like OR=0.01 → "99% lower", which is nonsensical.
  // Plausible ORs for disease outcomes: 0.1–10. For quantitative traits the
  // same field could be a beta anywhere from -5 to +5 (stored as-is, not
  // exponentiated), so values outside 0.5–2.0 are almost certainly betas.
  const isQuantitative = isQuantitativeTrait(trait);
  if (isQuantitative && (val < 0.5 || val > 2.0)) return null;
  // Non-quantitative extreme guard (unchanged)
  if (val < 0.01 || val > 100) return null;

  const { copiesCarried, allele } = association;

  // Genotype context suffix, e.g. "(2 copies of A)"
  const genotypeContext = copiesCarried !== null && allele
    ? ` **(${copiesCarried} cop${copiesCarried === 1 ? 'y' : 'ies'} of ${allele})**`
    : '';

  if (val > 1) {
    const pct = ((val - 1) * 100).toFixed(0);
    if (isQuantitative) {
      // Never show percentages for quantitative traits — the number has no
      // units and can't be interpreted without knowing the trait's SD scale.
      if (val >= 1.3) return `Associated with higher ${trait}${genotypeContext}`;
                      return `Slightly higher ${trait}${genotypeContext}`;
    } else {
      if (val >= 2)    return `${val.toFixed(1)}x increased odds of ${trait}${genotypeContext}`;
      if (val >= 1.3)  return `${pct}% higher odds of ${trait}${genotypeContext}`;
      if (val > 1.05)  return `Slightly higher odds of ${trait}${genotypeContext}`;
    }
  } else if (val < 1) {
    const pct = ((1 - val) * 100).toFixed(1);
    if (isQuantitative) {
      // Same reasoning — directional language only, no percentage
      if (val < 0.83) return `Associated with lower ${trait}${genotypeContext}`;
                      return `Slightly lower ${trait}${genotypeContext}`;
    } else {
      if (val <= 0.8) return `${pct}% lower odds of ${trait}${genotypeContext}`;
                      return `Slightly lower odds of ${trait}${genotypeContext}`;
    }
  }

  return null;
};

// ---------------------------------------------------------------------------
// Risk summary (used by SNPCard)
// ---------------------------------------------------------------------------

/**
 * Create a comprehensive risk summary with GWAS prioritisation.
 * Primary message uses the most impactful carried GWAS association;
 * falls back to ClinVar description if no significant GWAS exists.
 * GxE associations can still appear as detail lines with their conditional note.
 */
export const createRiskSummary = (snp) => {
  const interpretation = interpretClinicalSignificance(snp);

  const summary = {
    primaryMessage: '',
    riskLevel: interpretation.label,
    category: interpretation.category,
    details: [],
    icon: interpretation.icon
  };

  // Most impactful carried non-GxE association with meaningful OR deviation.
  // Sort by OR deviation from 1.0, preferring risk (OR > 1) on ties so that
  // a cancer risk isn't hidden behind a protective trait with a similar OR.
  const carriedAssocs = (snp.gwas?.topAssociations || []).filter(a => {
    if (a.gxe) return false;
    if (a.copiesCarried !== null && a.copiesCarried === 0) return false;
    const odds = a.userEffectiveOdds || a.orBeta;
    if (!odds || odds < 0.01 || odds > 100) return false;
    if (isQuantitativeTrait(a.trait) && (odds < 0.5 || odds > 2.0)) return false;
    return (odds > 1.2 || odds < 0.83);
  });
  const carriedAssoc = carriedAssocs.length > 0
    ? carriedAssocs.reduce((best, a) => {
        const aDev = Math.abs(1 - (a.userEffectiveOdds || a.orBeta));
        const bDev = Math.abs(1 - (best.userEffectiveOdds || best.orBeta));
        if (aDev > bDev) return a;
        if (aDev < bDev) return best;
        return (a.userEffectiveOdds || a.orBeta) > 1 ? a : best;
      })
    : null;

  if (carriedAssoc) {
    const riskMsg = generateRiskMessage(carriedAssoc, carriedAssoc.trait);
    if (riskMsg) summary.primaryMessage = riskMsg;
  }

  // If the best OR-based association was suppressed (e.g. quantitative beta),
  // build a directional message from any carried GWAS trait instead.
  // This prevents falling back to the generic ClinVar label when GWAS data
  // is present but numerically untrustworthy.
  if (!summary.primaryMessage && snp.gwas?.topAssociations?.length) {
    const anyCarried = snp.gwas.topAssociations.filter(a =>
      !a.gxe && (a.copiesCarried === null || a.copiesCarried > 0)
    );

    // Prefer a trait with a clear direction, fall back to first carried trait
    const directedAssoc = anyCarried.find(a => a.direction === 'increased' || a.direction === 'decreased')
      || anyCarried[0];

    if (directedAssoc) {
      const { copiesCarried, allele, direction, trait } = directedAssoc;
      const genotypeContext = copiesCarried !== null && allele
        ? ` **(${copiesCarried} cop${copiesCarried === 1 ? 'y' : 'ies'} of ${allele})**`
        : '';
      if (direction === 'increased') {
        summary.primaryMessage = `Associated with higher ${trait}${genotypeContext}`;
      } else if (direction === 'decreased') {
        summary.primaryMessage = `Associated with lower ${trait}${genotypeContext}`;
      } else {
        summary.primaryMessage = `GWAS association: ${trait}${genotypeContext}`;
      }
    }
  }

  // Fall back to ClinVar description
  if (!summary.primaryMessage) {
    summary.primaryMessage = interpretation.description;
  }

  // Add ClinVar conditions to details (avoid repeating what's in primaryMessage)
  if (snp.conditions) {
    const firstCondition = snp.conditions.toLowerCase().split(',')[0];
    if (!summary.primaryMessage.toLowerCase().includes(firstCondition)) {
      summary.details.push(`Associated with ${snp.conditions}`);
    }
  }

  // Add further carried GWAS traits as detail lines
  // GxE associations are included here but with their conditional note
  if (snp.gwas?.topAssociations) {
    snp.gwas.topAssociations
      .filter(a =>
        (a.copiesCarried === null || a.copiesCarried > 0) &&
        a !== carriedAssoc
      )
      .slice(0, 2)
      .forEach(a => {
        const msg = generateRiskMessage(a, a.trait);
        if (msg && !summary.details.includes(msg)) {
          summary.details.push(msg);
        }
      });
  }

  // Last-resort fallbacks
  if (!summary.primaryMessage && snp.summary) {
    summary.primaryMessage = snp.summary;
  }
  if (!summary.primaryMessage) {
    summary.primaryMessage = 'Genetic variant with available data';
  }

  return summary;
};

// ---------------------------------------------------------------------------
// Sorting & grouping
// ---------------------------------------------------------------------------

/**
 * Severity order for "harmful first" sort.
 * likely-inactive sits at the very bottom (0.5) so it doesn't
 * float to the top when the order is reversed for "beneficial first".
 */
const HARMFUL_FIRST_ORDER = {
  'pathogenic':        7,
  'likely-pathogenic': 6,
  'risk':              5,
  'drug-response':     4,
  'uncertain':         3,
  'neutral':           2,
  'protective':        1,
  'likely-inactive':   0,  // always last regardless of sort direction
  'other':             0,
};

/**
 * Explicit order for "beneficial first" — mirrors harmful-first but
 * puts protective/neutral at the top and likely-inactive still last.
 */
const BENEFICIAL_FIRST_ORDER = {
  'protective':        7,
  'neutral':           6,
  'uncertain':         5,
  'drug-response':     4,
  'risk':              3,
  'likely-pathogenic': 2,
  'pathogenic':        1,
  'likely-inactive':   0,  // always last
  'other':             0,
};

/** Secondary sort key: OR deviation from 1.0 (ignores GxE and beta-like values) */
const getOrImpact = (snp) => {
  const assoc = snp.gwas?.topAssociations?.find(x => {
    if (x.gxe) return false;
    if (x.copiesCarried !== null && x.copiesCarried === 0) return false;
    const odds = x.userEffectiveOdds || x.orBeta;
    return odds && odds >= 0.01 && odds <= 100;
  });
  return Math.abs(1 - (assoc?.userEffectiveOdds || 1));
};

/**
 * Sort by clinical importance — harmful variants first.
 * Secondary key: absolute OR deviation so higher-impact variants
 * within the same category bubble up.
 */
export const sortByClinicalImportance = (snps) => {
  return [...snps].sort((a, b) => {
    const aCategory = interpretClinicalSignificance(a).category;
    const bCategory = interpretClinicalSignificance(b).category;
    const aOrder = HARMFUL_FIRST_ORDER[aCategory] ?? 0;
    const bOrder = HARMFUL_FIRST_ORDER[bCategory] ?? 0;
    if (aOrder !== bOrder) return bOrder - aOrder;
    return getOrImpact(b) - getOrImpact(a);
  });
};

/**
 * Sort beneficial variants first — protective/neutral at the top,
 * likely-inactive always last regardless of direction.
 */
export const sortByBeneficialFirst = (snps) => {
  return [...snps].sort((a, b) => {
    const aCategory = interpretClinicalSignificance(a).category;
    const bCategory = interpretClinicalSignificance(b).category;
    const aOrder = BENEFICIAL_FIRST_ORDER[aCategory] ?? 0;
    const bOrder = BENEFICIAL_FIRST_ORDER[bCategory] ?? 0;
    if (aOrder !== bOrder) return bOrder - aOrder;
    return getOrImpact(b) - getOrImpact(a);
  });
};

/**
 * Group SNPs by risk category.
 */
export const groupByRiskCategory = (snps) => {
  const groups = {
    pathogenic: [],
    'likely-pathogenic': [],
    risk: [],
    protective: [],
    'drug-response': [],
    uncertain: [],
    neutral: [],
    'likely-inactive': [],
    other: []
  };

  snps.forEach(snp => {
    const { category } = interpretClinicalSignificance(snp);
    if (groups[category]) {
      groups[category].push(snp);
    } else {
      groups.other.push(snp);
    }
  });

  return groups;
};

/**
 * Get counts per risk category (used by FilterBar).
 */
export const getRiskCategoryCounts = (snps) => {
  const groups = groupByRiskCategory(snps);

  return {
    all: snps.length,
    pathogenic:           groups.pathogenic.length,
    'likely-pathogenic':  groups['likely-pathogenic'].length,
    risk:                 groups.risk.length,
    protective:           groups.protective.length,
    'drug-response':      groups['drug-response'].length,
    uncertain:            groups.uncertain.length,
    neutral:              groups.neutral.length,
    'likely-inactive':    groups['likely-inactive'].length,
    other:                groups.other.length
  };
};

export default {
  interpretClinicalSignificance,
  generateRiskMessage,
  createRiskSummary,
  sortByClinicalImportance,
  groupByRiskCategory,
  getRiskCategoryCounts,
  filterToCarriedAssociations,
  hasCarriedAssociations,
};